import { validateEmail, validatePhone } from 'src/components/Forms/validations';
import { ClientEmail, ClientPhone } from 'src/graphql/entities/client/client.types';
import type { ClientWithPartner } from 'src/store/modals';

export enum AddClientFormField {
  Partner = 'partner',
  FirstName = 'firstName',
  LastName = 'lastName',
  Emails = 'emails',
  PhoneNumbers = 'phoneNumbers',
  Notes = 'notes',
  MarketingOptOut = 'marketingOptOut',
}

export type ClientContactField = AddClientFormField.Emails | AddClientFormField.PhoneNumbers;

export interface ClientContactRow {
  value: string;
  valueId: string;
  labelId: number;
  isPrimary: boolean;
}

export interface AddClientFormValues {
  [AddClientFormField.Partner]: { id: number; label: string } | null;
  [AddClientFormField.FirstName]: string;
  [AddClientFormField.LastName]: string;
  [AddClientFormField.Emails]: ClientContactRow[];
  [AddClientFormField.PhoneNumbers]: ClientContactRow[];
  [AddClientFormField.Notes]: string;
  [AddClientFormField.MarketingOptOut]: boolean;
}

export const EMPTY_CONTACT_ROW: ClientContactRow = {
  value: '',
  valueId: '',
  labelId: 0,
  isPrimary: false,
};

const toContactRows = <T extends { id?: string; labelId?: number }>(
  items: Partial<T>[] | undefined,
  primaryId: string | null | undefined,
  getValue: (item: Partial<T>) => string,
): ClientContactRow[] =>
  (items ?? [])
    .map((item, index) => ({
      value: getValue(item),
      valueId: item.id ?? '',
      labelId: item.labelId ?? 0,
      isPrimary: item.id === primaryId || (!primaryId && index === 0),
    }))
    .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));

export const getAddClientDefaultValues = (
  client?: ClientWithPartner | null,
): AddClientFormValues => ({
  [AddClientFormField.Partner]: null,
  [AddClientFormField.FirstName]: client?.firstName ?? '',
  [AddClientFormField.LastName]: client?.lastName ?? '',
  [AddClientFormField.Emails]: toContactRows<ClientEmail>(
    client?.emails,
    client?.primaryClientEmailId,
    (email) => email.email ?? '',
  ),
  [AddClientFormField.PhoneNumbers]: toContactRows<ClientPhone>(
    client?.phoneNumbers,
    client?.primaryClientPhoneNumberId,
    (phone) => phone.number?.toString() ?? '',
  ),
  [AddClientFormField.Notes]: client?.notes ?? '',
  [AddClientFormField.MarketingOptOut]: client?.marketingOptOut ?? false,
});

const CONTACT_VALIDATION: Record<
  ClientContactField,
  { isValid: (value: string) => boolean; invalidMessage: string; duplicateMessage: string }
> = {
  [AddClientFormField.Emails]: {
    isValid: validateEmail,
    invalidMessage: 'Enter a valid email',
    duplicateMessage: 'This email is already listed',
  },
  [AddClientFormField.PhoneNumbers]: {
    isValid: validatePhone,
    invalidMessage: 'Enter a valid phone number',
    duplicateMessage: 'This phone number is already listed',
  },
};

export const validateContactRow =
  (field: ClientContactField, index: number) =>
  (
    value: string,
    values: Partial<Record<ClientContactField, ClientContactRow[]>>,
  ): string | true => {
    const { isValid, invalidMessage, duplicateMessage } = CONTACT_VALIDATION[field];
    if (!isValid(value)) {
      return invalidMessage;
    }
    const firstIndex = (values[field] ?? []).findIndex((row) => row.value === value);
    return firstIndex === index || duplicateMessage;
  };
